/**
 * Does the given ISO week-year hold 53 weeks (rather than 52)?
 *
 * True when Jan 1st is a Thursday, or when a leap year starts on a
 * Wednesday — the two cases that push a 53rd week into the year. 2026 is
 * one (2026-01-01 is a Thursday), so the AW26 window legitimately contains
 * `202653`.
 */
export function isoWeeksInYear(year: number): 52 | 53 {
  const jan1Day = new Date(Date.UTC(year, 0, 1)).getUTCDay();
  const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  // 4 = Thursday, 3 = Wednesday.
  return jan1Day === 4 || (isLeap && jan1Day === 3) ? 53 : 52;
}

/**
 * Enumerate every year-week between two endpoints inclusive.
 *
 * Year-weeks are encoded as `YYYYWW` integers (e.g. `202601` for the first
 * week of 2026). The roll-over honours {@link isoWeeksInYear}: a 53-week
 * year emits `YYYY53` before advancing. Skipping it dropped the week from
 * the grid entirely for windows straddling a 53-week year's end — the
 * planner could neither see nor edit it, while `/save` still shipped
 * whatever the server had stored there.
 */
export function enumerateYearWeeks(
  startYearWeek: number,
  endYearWeek: number
): number[] {
  if (endYearWeek < startYearWeek) return [];
  const result: number[] = [];
  let cursor = startYearWeek;
  while (cursor <= endYearWeek) {
    result.push(cursor);
    cursor = shiftYearWeek(cursor, 1);
  }
  return result;
}

/**
 * `yearWeek` moved by `weeks` ISO weeks, back for a negative count, across
 * year ends. A 53-week year counts its `YYYY53` ({@link isoWeeksInYear}).
 */
export function shiftYearWeek(yearWeek: number, weeks: number): number {
  let year = Math.floor(yearWeek / 100);
  let week = (yearWeek % 100) + weeks;
  while (week < 1) {
    year -= 1;
    week += isoWeeksInYear(year);
  }
  while (week > isoWeeksInYear(year)) {
    week -= isoWeeksInYear(year);
    year += 1;
  }
  return year * 100 + week;
}

/**
 * Today's ISO year-week, encoded as the same 6-digit `YYYYWW` integer used
 * throughout the range-plan grid (e.g. `202622` for week 22 of 2026).
 *
 * Uses the Thursday-anchor trick (ISO 8601): the week's Thursday determines
 * both the week number and the week-year — so a Sunday late in December can
 * legitimately belong to week 1 of the following year. Matches the cube
 * grid's `getISOWeek` / `getISOWeekYear` helpers (which are not exported from
 * `@appretio/cube`), and the backend's `yearweek_to_date` companion.
 */
export function getCurrentYearWeek(today: Date = new Date()): number {
  const utc = new Date(
    Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  );
  // Sunday → 7 so the offset arithmetic shifts to Thursday cleanly.
  const dayOfWeek = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - dayOfWeek);
  const isoYear = utc.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const dayDiffMs = utc.getTime() - yearStart.getTime();
  const isoWeek = Math.ceil((dayDiffMs / 86_400_000 + 1) / 7);
  return isoYear * 100 + isoWeek;
}

/**
 * Convert a 6-digit year-week (`YYYYWW`) to the ISO Monday of that week,
 * formatted as a `YYYY-MM-DD` string for a `<input type="date">`.
 *
 * Mirrors the backend's `yearweek_to_date` (v2 collections service) so
 * collection start/end defaults derived from the active window line up with
 * the server's window-overlap filter. Returns `""` for
 * non-positive / malformed input so callers can leave the field blank.
 */
export function yearWeekToIsoDate(yearWeek: number): string {
  if (!Number.isFinite(yearWeek) || yearWeek <= 0) return "";
  const year = Math.floor(yearWeek / 100);
  const week = yearWeek % 100;
  if (year < 1970 || week < 1 || week > isoWeeksInYear(year)) return "";
  // ISO week 1 contains Jan 4; the Monday of week 1 is jan4 - (weekday - 1).
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Weekday = jan4.getUTCDay() === 0 ? 7 : jan4.getUTCDay();
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - (jan4Weekday - 1));
  const target = new Date(week1Monday);
  target.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
  return target.toISOString().slice(0, 10);
}
